/**
 * Chamadas HTTP das manutenções.
 *
 * Papel: aqui ficam as oito chamadas de `/api/manutencoes/corretivas` e
 * `/api/manutencoes/preventivas` que a tela usa — listar, criar, alterar e
 * excluir cada um dos dois cadastros.
 *
 * Como usar: as duas listagens devolvem o array inteiro, e não uma página, já
 * na ordem da lista da origem — a de `posicao`, com o registro novo no fim e o
 * editado no lugar. Reordenar aqui desfaria essa ordem, que é a que a tela mostra
 * antes do primeiro clique num cabeçalho.
 */
import {
  type Corretiva,
  type NovaCorretiva,
  type NovaPreventiva,
  type Preventiva,
  corretivaSchema,
  preventivaSchema,
} from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

const listaDeCorretivasSchema = z.array(corretivaSchema);
const listaDePreventivasSchema = z.array(preventivaSchema);

/**
 * Lê as ocorrências corretivas.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As corretivas, cada uma com a residência aninhada.
 */
export function buscarCorretivas(sinal?: AbortSignal): Promise<Corretiva[]> {
  return clienteApi.get('/manutencoes/corretivas', { schema: listaDeCorretivasSchema, sinal });
}

/**
 * Cria uma ocorrência corretiva.
 *
 * @param dados - A ocorrência preenchida, já validada.
 * @returns A corretiva como ficou gravada.
 */
export function criarCorretiva(dados: NovaCorretiva): Promise<Corretiva> {
  return clienteApi.post('/manutencoes/corretivas', dados, { schema: corretivaSchema });
}

/**
 * Substitui todos os campos de uma corretiva.
 *
 * @param id - Identificador da linha em `manutencoes_corretivas`.
 * @param dados - A ocorrência inteira, e não apenas os campos alterados.
 * @returns A corretiva como ficou gravada.
 */
export function atualizarCorretiva(id: string, dados: NovaCorretiva): Promise<Corretiva> {
  return clienteApi.put(`/manutencoes/corretivas/${encodeURIComponent(id)}`, dados, {
    schema: corretivaSchema,
  });
}

/**
 * Apaga uma ocorrência corretiva.
 *
 * Não recebe schema porque a API responde 204 sem corpo.
 *
 * @param id - Identificador da linha em `manutencoes_corretivas`.
 */
export function excluirCorretiva(id: string): Promise<void> {
  return clienteApi.remover(`/manutencoes/corretivas/${encodeURIComponent(id)}`);
}

/**
 * Lê as rotinas preventivas.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As preventivas, cada uma com a residência aninhada.
 */
export function buscarPreventivas(sinal?: AbortSignal): Promise<Preventiva[]> {
  return clienteApi.get('/manutencoes/preventivas', {
    schema: listaDePreventivasSchema,
    sinal,
  });
}

/**
 * Cria uma rotina preventiva.
 *
 * @param dados - A rotina preenchida, já validada.
 * @returns A preventiva como ficou gravada.
 */
export function criarPreventiva(dados: NovaPreventiva): Promise<Preventiva> {
  return clienteApi.post('/manutencoes/preventivas', dados, { schema: preventivaSchema });
}

/**
 * Substitui todos os campos de uma preventiva.
 *
 * @param id - Identificador da linha em `manutencoes_preventivas`.
 * @param dados - A rotina inteira, e não apenas os campos alterados.
 * @returns A preventiva como ficou gravada.
 */
export function atualizarPreventiva(id: string, dados: NovaPreventiva): Promise<Preventiva> {
  return clienteApi.put(`/manutencoes/preventivas/${encodeURIComponent(id)}`, dados, {
    schema: preventivaSchema,
  });
}

/**
 * Apaga uma rotina preventiva.
 *
 * Não recebe schema porque a API responde 204 sem corpo.
 *
 * @param id - Identificador da linha em `manutencoes_preventivas`.
 */
export function excluirPreventiva(id: string): Promise<void> {
  return clienteApi.remover(`/manutencoes/preventivas/${encodeURIComponent(id)}`);
}
